import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownTrayIcon,
  Box,
  Button,
  Dialog,
  EyeIcon,
  Flex,
  IconButton,
  Image,
  Spinner,
  Text,
  Tooltip,
  XMarkIcon,
} from 'components'
import { useAlert } from 'components/providers'
import { FullMetadata } from 'firebase/storage'
import { useState } from 'react'
import {
  deleteFile,
  downloadFile,
  downloadFilesAsZip,
  fileExtensionColors,
  formatFileSize,
  getExtension,
  isImagePath,
} from 'utils'
import { getSessionMedia } from '../../utils'

interface Props {
  eventId?: string
  sessionId: string
  workspaceId: string
}

const defaultBadgeStyle = { bg: '#F5F5F5', color: '#616161' }

export default function SessionMediaViewer({ eventId, sessionId, workspaceId }: Props) {
  const queryClient = useQueryClient()
  const { confirm } = useAlert()
  const [selectedFile, setSelectedFile] = useState<{
    path: string
    url: string
    meta: FullMetadata
  } | null>(null)

  const queryKey = ['sessions', workspaceId, eventId, sessionId]
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getSessionMedia(workspaceId, sessionId, eventId),
  })
  const files = data ?? []

  async function handleDelete(file: { path: string; meta: FullMetadata }) {
    const confirmed = await confirm(`Delete "${file.meta.name}"?`)
    if (!confirmed) return
    await deleteFile(file.path)
    queryClient.invalidateQueries({ queryKey })
  }

  if (isLoading) return <Spinner />

  return (
    <>
      <Flex
        bg="gray.50"
        border="1px dashed"
        borderColor="gray.200"
        borderRadius="lg"
        direction="column"
        overflow="hidden"
      >
        {files.length > 0 && (
          <Flex direction="column" py={1}>
            {files.map(file => {
              const ext = (getExtension(file.meta.name ?? file.path) || '').toLowerCase()
              const badgeStyle = fileExtensionColors[ext] || defaultBadgeStyle
              const isImage = isImagePath(file.path)

              return (
                <Flex
                  key={file.path}
                  align="center"
                  gap={3}
                  px={4}
                  py={2.5}
                  _hover={{ bg: 'gray.100' }}
                  transition="background 150ms"
                >
                  <Box bg={badgeStyle.bg} borderRadius="sm" flexShrink={0} px={1.5} py={0.5}>
                    <Text
                      color={badgeStyle.color}
                      fontSize="10px"
                      fontWeight="700"
                      letterSpacing="0.5px"
                      lineHeight="1.2"
                      textTransform="uppercase"
                    >
                      {ext || '?'}
                    </Text>
                  </Box>

                  <Text
                    flex={1}
                    fontSize="sm"
                    fontWeight="500"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {file.meta.name ?? file.path.split('/').pop()}
                  </Text>

                  <Text color="gray.500" flexShrink={0} fontSize="xs" fontWeight="500">
                    {formatFileSize(file.meta.size ?? 0)}
                  </Text>

                  <Flex flexShrink={0} gap={0.5}>
                    {isImage && (
                      <Tooltip
                        content={
                          <Image
                            alt={file.meta.name ?? 'Preview'}
                            borderRadius="sm"
                            maxH="200px"
                            maxW="200px"
                            objectFit="cover"
                            src={file.url}
                          />
                        }
                        contentProps={{ p: 0, overflow: 'hidden' }}
                        hideArrow
                      >
                        <IconButton
                          aria-label="Preview file"
                          icon={<EyeIcon size="sm" />}
                          onClick={() => setSelectedFile(file)}
                          size="xs"
                          variant="ghost"
                        />
                      </Tooltip>
                    )}
                    <Tooltip content="Download">
                      <IconButton
                        aria-label="Download file"
                        icon={<ArrowDownTrayIcon size="sm" />}
                        onClick={() => downloadFile(file.url, file.meta.name ?? 'file')}
                        size="xs"
                        variant="ghost"
                      />
                    </Tooltip>
                    <Tooltip content="Delete">
                      <IconButton
                        aria-label="Delete file"
                        icon={<XMarkIcon size="sm" />}
                        onClick={() => handleDelete(file)}
                        size="xs"
                        variant="ghost"
                      />
                    </Tooltip>
                  </Flex>
                </Flex>
              )
            })}
          </Flex>
        )}

        {files.length === 0 && (
          <Flex justify="center" py={4}>
            <Text color="gray.400" fontStyle="italic" textStyle="bodySmall">
              No attachments
            </Text>
          </Flex>
        )}

        <Dialog
          bodyProps={{ p: 2 }}
          onClose={() => setSelectedFile(null)}
          open={!!selectedFile}
          title={
            selectedFile && (
              <Flex alignItems="center" gap={2} justifyContent="space-between" w="full">
                <Text flex={1} opacity={0.7} textStyle="bodySmall" truncate>
                  {selectedFile.meta.name}
                </Text>
                <Tooltip content="Download">
                  <IconButton
                    aria-label="Download file"
                    icon={<ArrowDownTrayIcon />}
                    onClick={() => downloadFile(selectedFile.url, selectedFile.meta.name ?? 'file')}
                  />
                </Tooltip>
              </Flex>
            )
          }
        >
          <Image key={selectedFile?.path} objectFit="cover" src={selectedFile?.url} />
        </Dialog>
      </Flex>

      {files.length > 1 && (
        <Button
          leftIcon={<ArrowDownTrayIcon />}
          onClick={() => downloadFilesAsZip(files, `${sessionId}-media.zip`)}
        >
          Download all attachments
        </Button>
      )}
    </>
  )
}
